import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Store, select } from '@ngrx/store';
import { NgDynamicFormComponent } from '@tesla/ng-dynamic-form';
import { INgDynamicFormJson } from '@tesla/ng-dynamic-form/lib/models/ng-dynamic-form.model';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { UserDetails } from 'app/profile/models/user.model';
import { PostEvent } from 'app/profile/store/app.actions';
import { SelectCurrentUser, SelectEventData } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { I18nService } from 'app/shared/services/i18n.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import _ from 'lodash';
import { ProfileService } from 'app/profile/services/profile.service';

@Component({
  selector: 'app-name-change-form',
  templateUrl: './name-change-form.component.html',
  styleUrls: ['./name-change-form.component.scss']
})
export class NameChangeFormComponent implements OnInit, OnDestroy {
  @ViewChild('nameChangeForm', { static: false }) public nameChangeForm: NgDynamicFormComponent;
  supplierCode: string;
  requestId: string;
  mode: "view" | "edit";
  formSchema: INgDynamicFormJson;
  formData: any = {};
  userDetails: UserDetails = null;
  oldLegalName: string;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  subscription = new Subscription();
  getRequestSub: Subscription;
  translationSub: Subscription;
  submitSub: Subscription;
  file = {};
  minDate: Date = new Date();
  changeTypeList: {displayName: string, value: string}[] = [
    { 
      displayName: this.translate.get("Update to organization's name"), 
      value: "details" 
    },{ 
      displayName: this.translate.get("Update to organization's tax information"), 
      value: "tax" 
    }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {supplierCode: string, requestId: string, mode: "view" | "edit"},
    private dialogRef: MatDialogRef<NameChangeFormComponent>,
    public translate: I18nService,
    private matDialog: MatDialog,
    private store: Store<SupplierState>,
    private fileUploadService: FileUploadService,
    private translocoService: TranslocoService,
    private profileService: ProfileService,
  ) { 
    this.supplierCode = data?.supplierCode;
    this.requestId = data?.requestId;
    this.mode = data?.mode;
    this.minDate.setFullYear(this.minDate.getFullYear() - 1);
  }

  ngOnInit(): void {
    if(this.requestId) {
      this.getRequestSub = this.profileService.getRequestByRequestId(this.requestId).subscribe(
        res => {
          if(res) {
            this.formData = {
              ChangeType: res.changeType,
              Comment: res.comments,
              EffectiveDate: res.effectiveDate,
              Reason: res.reason
            }
            this.initNameChangeForm();
          }
        }
      )
    } else {
      this.mode = "edit";
      this.initNameChangeForm();
    }
  }

  initNameChangeForm() {
    this.initTranslation();
    this.formSchema = this.getNameChangeFormSchema()
  }

  initTranslation() {
    this.destroyed$.next(true);
    this.translationSub = this.translocoService.langChanges$.subscribe((res) => {
      if (_.isEmpty(this.translocoService.getTranslation(res))) {
          this.subscription = this.translocoService.events$
              .pipe(
                  filter((e) => e.type === 'translationLoadSuccess'),
                  takeUntil(this.destroyed$),
              )
              .subscribe((obj) => {
                  this.updateLocale(res);
              });
      } else {
          this.updateLocale(res);
      }
    });
  }

  updateLocale(newLanguage) {
    const newFile = this.translocoService.getTranslation(newLanguage);
    if (!newFile) {
        return;
    }
    this.file = newFile;
  }

  onDynamicChange(event) {

  }

  close() {
    document
      .getElementsByClassName('animate__animated')[0]
      .classList.add('animate__slideOutRight');
    setTimeout(() => {
      this.dialogRef.close();
    }, 1000);
  }

  async submit() {
    this.nameChangeForm?.dynamicFormGroup?.markAllAsTouched();
    if(!this.nameChangeForm?.dynamicFormGroup?.valid) return;
    if (!(this.nameChangeForm?.dynamicFormGroup?.value?.dynamicFormArrays?.length && this.nameChangeForm?.dynamicFormGroup?.value?.dynamicFormArrays[0]?.formArray.length)) return;
    let data = this.nameChangeForm.dynamicFormGroup.value.dynamicFormArrays[0].formArray[0];
    delete data.banner;
    this.store.dispatch(new PostEvent({
      Name: 'Name Or TIN Change Requested',
      Properties: data,
      ReferenceIdentifier: this.supplierCode
    }));
    this.submitSub = this.store.select(SelectEventData).subscribe((evt: any) => {
      if (evt) {
        document
          .getElementsByClassName('animate__animated')[0]
          .classList.add('animate__slideOutRight');
        setTimeout(() => {
          this.dialogRef.close(true);
        }, 1000);
      }
    });
  }

  getNameChangeFormSchema(): INgDynamicFormJson {
    return {
      formHeader: null,
      formBody: [
        {
          sectionHeader: { displayText: "", i18nKey: "" },
          sectionDescription: null,
          isDisplayedOnPdf: true,
          isDisplayedOnUI: true,
          isCollapsable: false,
          fields: [
            {
              type: "alert",
              displayName: { displayText: "", i18nKey: "" },
              key: "banner",
              value: this.translate.get("In order to update your organization's name on file with Tesla, we require further information regarding this change. If needed, please update or provide your organization's information, and attach supporting documents that reflect these changes - if applicable - within the assigned tasks after completion of this questionnaire."),
              tooltip: "",
              isReadonly: true,
              isDisplayed: true,
              colSize: 12,
              props: {
                isRequired: false,
                alertType: "info"
              }
            },
            {
              type: "multiAutocomplete",
              displayName: { displayText: "Please confirm what information your organization would like to update on file with Tesla:", i18nKey: "nameChangeTypeLabel" },
              key: "ChangeType",
              value: "",
              tooltip: "",
              isReadonly: this.mode == "view",
              isDisplayed: true,
              colSize: 8,
              props: {
                isRequired: true,
                isSelectAllDisabled: true,
                disableSearch: true,
                initialCollectionLoading: true,
                maxWidth: "90%",
                customOptions: this.changeTypeList,
                // contentWidth: "content",
                // fullWidth: true
              }
            },
            {
              type: "date",
              displayName: { displayText: "When did this change take effect?", i18nKey: "nameChangeEffectiveDateLabel" },
              key: "EffectiveDate",
              value: "",
              tooltip: "",
              isReadonly: this.mode == "view",
              isDisplayed: true,
              colSize: 4,
              props: {
                isRequired: true,
                min: this.minDate,
                max: new Date(),
                // fullWidth: true
              }
            },
            {
              type: "dropdown",
              displayName: { displayText: "Please provide a reason for your organization's updates to your information on file with Tesla:", i18nKey: "nameChangeReasonLabel" },
              key: "Reason",
              value: "",
              tooltip: "",
              isReadonly: this.mode == "view",
              isDisplayed: true,
              colSize: 8,
              props: {
                isRequired: true,
                isSelectAllDisabled: true,
                initialCollectionLoading: true,
                maxWidth: "90%",
                options: [
                  { 
                    displayName: this.translate.get("Company acquisition, reorganization, or merger"), 
                    value: "Company acquisition, reorganization, or merger" 
                  },
                  { 
                    displayName: this.translate.get("Changing of organizational structures or company re-branding"), 
                    value: "Changing of organizational structures or company re-branding" 
                  },
                  { 
                    displayName: this.translate.get("Incorrect information on file"), 
                    value: "Incorrect information on file" 
                  },
                  { 
                    displayName: this.translate.get("Other (Please explain)"), 
                    value: "Other" 
                  },
                ],
                // contentWidth: "content",
                // fullWidth: true
              }
            },
            {
              type: "textarea",
              displayName: { displayText: "Please provide other reasons:", i18nKey: "nameChangeCommentLabel" },
              key: "Comment",
              value: "",
              tooltip: "",
              isReadonly: this.mode == "view",
              isDisplayed: true,
              displayedBy: '(formData) => {return formData?.Reason == "Other"}',
              colSize: 8,
              props: {
                isRequired: true,
                maxChar: 200
              }
            }
          ]
        }
      ],
      formAction: [],
      validationFn: "",
      validationApi: {},
      availableLangs: ["en-US"]
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.subscription?.unsubscribe();
    this.getRequestSub?.unsubscribe();
    this.translationSub?.unsubscribe();
    this.submitSub?.unsubscribe();
  }
            
  getDocumentSectionDescription():string {
    return `<p>${this.translate.get("These are examples of accepted documents. Please choose and upload one of the following:")}</p>
            <ul>
              <li>${this.translate.get("Your organization's official announcement letter regarding the name change or restructure")}</li>
              <li>${this.translate.get("Posted mail displaying the name and address of your organization")}</li>
              <li>${this.translate.get("Lease agreement or mortgage statement")}</li>
              <li>${this.translate.get("Property tax receipt")}</li>
              <li>${this.translate.get("Utility bill")}</li>
            </ul>
            <p>${this.translate.get("The document must include the following:")}</p>
            <ul>
              <li>${this.translate.get("Your organization's new name")}</li>
              <li>${this.translate.get("Your organization's remittance address")}</li>
            </ul>`
  }
}
